import React, { useState } from 'react';

const HowItWorksPage = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const steps = [
    {
      id: 1,
      icon: "🔗",
      title: "Connect Your Facebook Ads Account",
      shortTitle: "Connect",
      description: "Link your Facebook Ads account in about 2 minutes. Secure OAuth connection - we never see your password or billing info.",
      details: [
        "One-click OAuth connection to Facebook Ads",
        "We automatically sync ALL your campaign data (even historical)",
        "100% secure - read-only access to campaign performance",
        "No manual CSV exports, no data entry, zero hassle"
      ],
      reality: "Simply click 'Connect Facebook' and authorize. That's it. We handle everything else.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      id: 2,
      icon: "🔄",
      title: "AI Syncs & Analyzes Your Data",
      shortTitle: "Sync",
      description: "Our AI pulls your campaign data and creates a comprehensive performance snapshot with intelligent severity ratings.",
      details: [
        "Automatically syncs campaigns, ad sets, and performance metrics",
        "AI analyzes 50+ performance signals per campaign",
        "Smart severity rating: Champion, Healthy, Monitoring, or Critical",
        "Identifies waste patterns and budget optimization opportunities"
      ],
      reality: "Click 'Refresh Data' anytime to sync latest performance. AI analyzes everything and gives you instant insights.",
      color: "from-purple-500 to-pink-500"
    },
    {
      id: 3,
      icon: "📊",
      title: "Get Your Performance Dashboard",
      shortTitle: "Dashboard",
      description: "See all your campaigns at a glance with AI-powered performance ratings, waste detection, and actionable insights.",
      details: [
        "Campaign Detective shows performance across all campaigns",
        "Color-coded severity: Green = Champion, Orange = Critical",
        "See waste estimates, CTR, CPC, conversions at a glance",
        "Filter by platform, status, performance, or search by name"
      ],
      reality: "Your dashboard loads instantly with every campaign rated. Know exactly which campaigns need attention and which to scale.",
      color: "from-orange-500 to-red-500"
    },
    {
      id: 4,
      icon: "🔬",
      title: "Deep Dive with AI Analysis",
      shortTitle: "Deep Dive",
      description: "Click any campaign for objective-aware AI analysis. Get strategic insights powered by Google Search grounding for real-time benchmarks.",
      details: [
        "Deep Analysis considers your campaign objective (Leads vs Traffic vs Sales)",
        "AI compares your performance to industry benchmarks",
        "Get specific recommendations: 'Reduce CPC by 30%' not vague advice",
        "See performance timeline and trend analysis"
      ],
      reality: "Stop guessing why campaigns fail. AI tells you exactly what's wrong and how to fix it based on your specific objective.",
      color: "from-green-500 to-emerald-500"
    },
    {
      id: 5,
      icon: "📝",
      title: "Log Decisions with Decision Tracker",
      shortTitle: "Track Decisions",
      description: "Document every marketing decision you make. Track what you changed, why you changed it, and what you expected to happen.",
      details: [
        "Log campaign changes: budget adjustments, targeting updates, creative swaps",
        "Record your expected outcome before implementing",
        "Choose from 8 objective types (Conversion, Lead Gen, Traffic, etc.)",
        "Get AI-powered strategic insights for each decision"
      ],
      reality: "No more 'who changed what?' Build a knowledge base of every decision your team makes.",
      color: "from-indigo-500 to-purple-500"
    },
    {
      id: 6,
      icon: "🎯",
      title: "Measure Outcomes & Get Accountability Scores",
      shortTitle: "Measure",
      description: "After implementing, log actual results. AI scores your decision accuracy and helps you learn what actually works.",
      details: [
        "Record actual metrics: cost impact, conversions, ROAS, etc.",
        "AI calculates accountability score based on prediction accuracy",
        "See if your decision improved or hurt performance",
        "Build institutional knowledge from wins AND losses"
      ],
      reality: "Finally know if your 'optimization' actually worked or just made things worse. Data-driven learning at scale.",
      color: "from-yellow-500 to-orange-500"
    },
    {
      id: 7,
      icon: "🚀",
      title: "Scale What Works, Kill What Doesn't",
      shortTitle: "Optimize",
      description: "Armed with data and AI insights, you'll make confident decisions. Scale winners, pause losers, never repeat mistakes.",
      details: [
        "Identify Champion campaigns and scale their budgets",
        "Pause or fix Critical campaigns before they waste more money",
        "Export decision reports as PDFs for team alignment",
        "Real-time alerts when campaign performance shifts"
      ],
      reality: "You'll become the marketer who always knows what to do next. No guessing, no panic, just data-driven confidence.",
      color: "from-pink-500 to-red-500"
    }
  ];

  const features = [
    {
      icon: "🔬",
      title: "Campaign Detective",
      description: "AI analyzes your Facebook campaigns and rates performance: Champion, Healthy, Monitoring, or Critical",
      impact: "Instant performance visibility"
    },
    {
      icon: "🎯",
      title: "Waste Hunter",
      description: "Cross-campaign waste detection analyzing patterns across all campaigns to identify audience overlap and budget leaks",
      impact: "Finds hidden waste patterns"
    },
    {
      icon: "📊",
      title: "Deep Campaign Analysis",
      description: "Objective-aware AI deep dive with competitive analysis, audience psychology, risk assessment, and 90-day roadmaps",
      impact: "Strategic intelligence engine"
    },
    {
      icon: "💬",
      title: "Aura AI Assistant",
      description: "Chat with your AI marketing co-pilot for instant answers about campaign performance and strategic recommendations",
      impact: "Real-time marketing guidance"
    },
    {
      icon: "🌐",
      title: "Platform Intelligence",
      description: "AI recommends optimal platform mix across 195+ countries with budget allocation and multi-currency support (180+ currencies)",
      impact: "Global optimization"
    },
    {
      icon: "🌍",
      title: "Global Market Intelligence",
      description: "Real-time market analysis across 50+ industries in 195+ countries with competitive intelligence and market entry strategies",
      impact: "Strategic market analysis"
    },
    {
      icon: "✍️",
      title: "Content Generation Engine",
      description: "AI copywriter trained on 10+ proven frameworks generates high-converting copy for 30+ content types with psychological triggers",
      impact: "Psychology-driven copywriting"
    },
    {
      icon: "📈",
      title: "Decision Tracking System",
      description: "Log every marketing decision, track outcomes vs predictions, and get AI accountability scoring to build institutional knowledge",
      impact: "Complete team accountability"
    }
  ];

  const realResults = [
    {
      metric: "$2M+",
      label: "Waste Detected",
      description: "In a single campaign analysis",
      icon: "💰"
    },
    {
      metric: "99.2%",
      label: "AI Accuracy",
      description: "Campaign performance predictions",
      icon: "🎯"
    },
    {
      metric: "73%",
      label: "Audience Overlap",
      description: "Found and fixed in real campaign",
      icon: "👥"
    },
    {
      metric: "340%",
      label: "ROAS Improvement",
      description: "Average after optimization",
      icon: "📈"
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 py-4 border-b border-white/10 bg-black/95 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <a href="/" className="flex items-center gap-2">
              <span className="text-2xl">🔬</span>
              <span className="text-2xl font-black text-orange-400">WhyDidItDie</span>
            </a>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-6">
              <a href="/#modules" className="text-white/80 hover:text-orange-400 transition-colors">Features</a>
              <a href="/pricing" className="text-white/80 hover:text-orange-400 transition-colors">Pricing</a>
              <a href="/how-it-works" className="text-orange-400 font-bold">How It Works</a>
              <a href="/integrations" className="text-white/80 hover:text-orange-400 transition-colors">Integrations</a>
              <a href="https://app.whydiditdie.com/login" className="text-white/80 hover:text-orange-400 transition-colors">Login</a>
              <a href="https://app.whydiditdie.com/signup" className="bg-gradient-to-r from-orange-400 to-red-500 px-4 py-2 rounded-lg font-bold hover:shadow-lg transition-all">
                Start Free Trial
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden text-white text-2xl"
            >
              {isMenuOpen ? '✕' : '☰'}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-4">
              <a href="/#modules" className="block text-white/80 hover:text-orange-400 py-2">Features</a>
              <a href="/pricing" className="block text-white/80 hover:text-orange-400 py-2">Pricing</a>
              <a href="/how-it-works" className="block text-orange-400 font-bold py-2">How It Works</a>
              <a href="/integrations" className="block text-white/80 hover:text-orange-400 py-2">Integrations</a>
              <a href="https://app.whydiditdie.com/login" className="block text-white/80 hover:text-orange-400 py-2">Login</a>
              <a href="https://app.whydiditdie.com/signup" className="block bg-gradient-to-r from-orange-400 to-red-500 px-4 py-3 rounded-lg font-bold text-center">
                Start Free Trial
              </a>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 px-4 text-center bg-gradient-to-b from-orange-400/10 via-transparent to-transparent">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-orange-400/20 border border-orange-400/30 rounded-full px-4 py-2 text-sm font-semibold text-orange-300 mb-6">
            <span>🎯</span>
            The Real Process Behind AI Marketing Intelligence
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight">
            How WhyDidItDie Actually Works
          </h1>
          
          <p className="text-xl text-white/80 mb-8 leading-relaxed">
            No BS. No exaggerations. This is the <strong className="text-orange-400">exact step-by-step process</strong> of how our AI analyzes your campaigns, 
            detects waste, and helps you make better marketing decisions.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
            {realResults.map((result, idx) => (
              <div key={idx} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6 text-center">
                <div className="text-3xl mb-2">{result.icon}</div>
                <div className="text-3xl font-black text-orange-400 mb-2">{result.metric}</div>
                <div className="text-sm font-bold text-white mb-1">{result.label}</div>
                <div className="text-xs text-white/60">{result.description}</div>
              </div>
            ))}
          </div>

          <div className="bg-purple-500/10 border border-purple-400/30 rounded-2xl p-6 text-left">
            <div className="flex items-start gap-4">
              <span className="text-3xl">😏</span>
              <div>
                <h3 className="text-lg font-bold text-purple-300 mb-2">The "Yeah Right" Guarantee</h3>
                <p className="text-white/80 leading-relaxed">
                  If we don't find waste in 7 days, we'll refund you.<br />
                  <span className="text-purple-300 font-semibold italic">(Spoiler: We always find waste. Always.)</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7-Step Process */}
      <section className="py-20 px-4 bg-gradient-to-b from-black to-zinc-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black text-center mb-4">
            The Complete 7-Step Workflow
          </h2>
          <p className="text-lg text-white/70 text-center mb-12 max-w-3xl mx-auto">
            From connecting your account to scaling winners - here's exactly what happens when you use WhyDidItDie
          </p>

          {/* Step Navigation */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-12">
            {steps.map((step) => (
              <button
                key={step.id}
                onClick={() => setActiveStep(step.id)}
                className={`p-4 rounded-xl border-2 transition-all ${
                  activeStep === step.id
                    ? `bg-gradient-to-r ${step.color} border-white/30 scale-105`
                    : 'bg-white/5 border-white/10 hover:bg-white/10'
                }`}
              >
                <div className="text-3xl mb-2">{step.icon}</div>
                <div className="text-xs font-bold opacity-70 mb-1">Step {step.id}</div>
                <div className="text-sm font-bold">{step.shortTitle}</div>
              </button>
            ))}
          </div>

          {/* Active Step Content */}
          {steps.map((step) => (
            activeStep === step.id && (
              <div key={step.id} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 md:p-12">
                <div className="flex items-start gap-6 mb-8">
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-r ${step.color} flex items-center justify-center text-4xl flex-shrink-0`}>
                    {step.icon}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-bold text-orange-400 mb-2">STEP {step.id} OF 7</div>
                    <h3 className="text-3xl font-black mb-4">{step.title}</h3>
                    <p className="text-lg text-white/80 leading-relaxed">{step.description}</p>
                  </div>
                </div>

                <div className="bg-black/40 rounded-xl p-6 mb-6">
                  <h4 className="text-lg font-bold text-orange-400 mb-4">What Actually Happens:</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    {step.details.map((detail, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <span className="text-green-400 text-xl font-bold flex-shrink-0">✓</span>
                        <span className="text-white/90">{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">💡</span>
                    <div>
                      <h4 className="font-bold text-green-400 mb-2">In Plain English:</h4>
                      <p className="text-white/90 leading-relaxed">{step.reality}</p>
                    </div>
                  </div>
                </div>
              </div>
            )
          ))}

          {/* Process Timeline */}
          <div className="mt-12 bg-black/40 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-center mb-8 text-orange-400">Complete Timeline View:</h3>
            <div className="relative">
              <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 via-purple-500 to-red-500 transform -translate-x-1/2"></div>
              
              <div className="space-y-8">
                {steps.map((step, idx) => (
                  <div key={step.id} className="flex items-center gap-4">
                    <div className={`flex-1 ${idx % 2 === 0 ? 'text-right' : 'order-2'}`}>
                      <div className={`inline-block bg-white/5 border border-white/10 rounded-xl p-4 ${idx % 2 === 0 ? '' : 'text-left'}`}>
                        <div className="text-xs font-bold text-orange-400 mb-1">STEP {step.id}</div>
                        <div className="font-bold">{step.shortTitle}</div>
                      </div>
                    </div>
                    <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${step.color} flex items-center justify-center text-2xl z-10 flex-shrink-0`}>
                      {step.icon}
                    </div>
                    <div className={`flex-1 ${idx % 2 === 1 ? 'order-1' : ''}`}></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Overview */}
      <section className="py-20 px-4 bg-gradient-to-b from-zinc-900 to-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black text-center mb-4">
            8 AI Engines Working for You
          </h2>
          <p className="text-lg text-white/70 text-center mb-12 max-w-3xl mx-auto">
            Each module is designed to solve a specific problem marketers face every day
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <div key={idx} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:-translate-y-2 transition-all">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-white/70 mb-4 leading-relaxed text-sm">{feature.description}</p>
                <div className="bg-orange-400/20 border border-orange-400/30 rounded-lg px-3 py-2 text-sm font-bold text-orange-300 inline-block">
                  {feature.impact}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Video Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-black to-zinc-900">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black text-center mb-4">
            See Decision Tracker in Action
          </h2>
          <p className="text-lg text-white/70 text-center mb-12">
            Watch how to log marketing decisions, track outcomes, and build your institutional knowledge
          </p>

          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 mb-8">
            <div className="aspect-video rounded-xl overflow-hidden mb-6">
              <iframe
                src="https://youtube.com/embed/wLOqKQgBjxQ"
                title="WhyDidItDie Decision Tracker Demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-full h-full"
              ></iframe>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 bg-black/40 rounded-xl p-4">
                <span className="text-2xl">📝</span>
                <span className="text-sm">Log every campaign decision</span>
              </div>
              <div className="flex items-center gap-3 bg-black/40 rounded-xl p-4">
                <span className="text-2xl">📊</span>
                <span className="text-sm">Track actual outcomes vs predictions</span>
              </div>
              <div className="flex items-center gap-3 bg-black/40 rounded-xl p-4">
                <span className="text-2xl">🧠</span>
                <span className="text-sm">Get AI accountability scores</span>
              </div>
              <div className="flex items-center gap-3 bg-black/40 rounded-xl p-4">
                <span className="text-2xl">🎯</span>
                <span className="text-sm">Build your winning playbook</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-purple-600 via-purple-500 to-pink-500 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-[-50px] left-[-50px] w-[200px] h-[200px] bg-white/10 rounded-full animate-pulse" />
          <div className="absolute bottom-[-100px] right-[-80px] w-[300px] h-[300px] bg-white/10 rounded-full animate-pulse" />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-black mb-6">
            Ready to Stop Wasting Ad Spend?
          </h2>
          <p className="text-xl mb-8 text-white/95 leading-relaxed">
            Join thousands of marketers using AI to detect waste, track decisions, and scale what works.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <a href="https://app.whydiditdie.com/signup" className="bg-white text-purple-600 px-8 py-4 rounded-2xl font-black text-lg hover:scale-105 transition-all shadow-2xl">
              🚀 Start Free 2-Days Trial
            </a>
            <button className="bg-white/10 backdrop-blur-md text-white border-2 border-white/30 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-white/20 transition-all">
              📞 Schedule Demo
            </button>
          </div>

          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-black">✓</div>
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-black">✓</div>
              <span>Full platform access</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-black">✓</div>
              <span>Find waste in 10 minutes</span>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-6 py-4 inline-flex items-center gap-8">
            <div className="text-center">
              <div className="text-2xl font-black">10,000+</div>
              <div className="text-xs uppercase tracking-wide opacity-80">Active Users</div>
            </div>
            <div className="w-px h-10 bg-white/30"></div>
            <div className="text-center">
              <div className="text-2xl font-black">$2M+</div>
              <div className="text-xs uppercase tracking-wide opacity-80">Waste Detected</div>
            </div>
            <div className="w-px h-10 bg-white/30"></div>
            <div className="text-center">
              <div className="text-2xl font-black">99.2%</div>
              <div className="text-xs uppercase tracking-wide opacity-80">AI Accuracy</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-12 px-4 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl">🔬</span>
                <span className="text-xl font-black text-orange-400">WhyDidItDie</span>
              </div>
              <p className="text-sm text-white/60 leading-relaxed">
                AI-powered marketing intelligence that detects waste and helps you make better decisions.
              </p>
            </div>

            <div>
              <h4 className="font-bold mb-4">Product</h4>
              <div className="space-y-2 text-sm text-white/60">
                <a href="/#modules" className="block hover:text-orange-400">Features</a>
                <a href="/pricing" className="block hover:text-orange-400">Pricing</a>
                <a href="/how-it-works" className="block hover:text-orange-400">How It Works</a>
                <a href="/integrations" className="block hover:text-orange-400">Integrations</a>
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-4">Company</h4>
              <div className="space-y-2 text-sm text-white/60">
                <a href="/about" className="block hover:text-orange-400">About</a>
                <a href="/blog" className="block hover:text-orange-400">Blog</a>
                <a href="/contact" className="block hover:text-orange-400">Contact</a>
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-4">Follow Us</h4>
              <div className="grid grid-cols-2 gap-2">
                <a href="https://www.instagram.com/ai_whydiditdie" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-white/60 hover:text-orange-400">
                  <span>📸</span> Instagram
                </a>
                <a href="https://x.com/whydiditdie" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-white/60 hover:text-orange-400">
                  <span>𝕏</span> Twitter
                </a>
                <a href="https://www.facebook.com/whydiditdie" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-white/60 hover:text-orange-400">
                  <span>👍</span> Facebook
                </a>
                <a href="https://www.tiktok.com/@app.whydiditdie" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-white/60 hover:text-orange-400">
                  <span>🎵</span> TikTok
                </a>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-white/10 text-center text-sm text-white/60">
            © {new Date().getFullYear()} WhyDidItDie. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HowItWorksPage;